import { UMAMI_WEBSITE_ID } from "../env";

/**
 * Site-usage analytics — self-hosted Umami, wired per architecture §16g **A79**
 * and ticket T-263.
 *
 * ── What A79 bought, and what this file has to keep true ─────────────────────
 *
 * A79 refused GTM permanently (ruling 2), refused GA4-by-Measurement-Protocol
 * (ruling 3), and made `script-src 'self'` movable only by a named amendment
 * (ruling 1). Aaron chose the recommendation: **self-hosted Umami v2 on
 * Railway, tracker served from our own origin.**
 *
 * So there is no third-party origin anywhere in this feature. The script is
 * `/u/script.js` on *this* origin and the beacon goes to `/u/api/send` on *this*
 * origin; `infra/web/Caddyfile` reverse-proxies exactly those two paths onto
 * this environment's own Umami service, and the upstream is derived at build
 * time from `ANALYTICS_UPSTREAM_ORIGIN` — a Railway service reference resolved
 * per environment (§16m A100). The enforced CSP (A65) is therefore **unchanged
 * by this feature** — `'self'` already covers both — and A66's "one derived
 * list, nothing hand-maintained per environment" property is untouched.
 *
 * ── Why injection lives in code rather than in `index.html` ──────────────────
 *
 * Two reasons, one of them A79's:
 *
 * 1. **The gate.** An environment with no `VITE_UMAMI_WEBSITE_ID` must load
 *    nothing at all — not a script tag that 404s, not a request. A static tag in
 *    `index.html` cannot be conditional. Production is exactly that environment
 *    today.
 * 2. **A79 ruling 4's shape, kept available.** For any vendor that sets an
 *    identifier or transmits to a third party, the gate is *non-loading*, never
 *    "consent mode denied". Umami does neither of those things, so no consent is
 *    required and no banner exists (that is the whole point of the choice) — but
 *    the mechanism that a consent gate would need is the one already used here,
 *    so honouring ruling 4 later would be a condition, not a rewrite.
 *
 * ── Cookies and storage: none, and it is the tracker's own doing ─────────────
 *
 * Read from the pinned tracker source (`umami-software/umami@v2.20.2`,
 * `src/tracker/index.js`), not assumed:
 *
 * - the beacon is `fetch(endpoint, { credentials: "omit", … })` — no cookie is
 *   sent and none is set;
 * - `localStorage` is **read** once, for the `umami.disabled` opt-out key, and
 *   never written;
 * - the only stitching value is `cache`, an in-memory JS variable echoed back in
 *   an `x-umami-cache` header and lost on reload;
 * - `identity` is populated only by an explicit `umami.identify()` call. **We
 *   never call it, and nothing in this app should** — that is the one line in
 *   this integration that would turn an anonymous counter into an identifier and
 *   make §4 of the privacy policy false.
 *
 * Server side, `POST /api/send` derives `sessionId = uuid(websiteId, ip,
 * userAgent, hash(start-of-month))` and stores browser/os/device/screen/
 * language/country/region/city. The raw IP is hashed into that id and used for
 * the geo lookup; it is never persisted, and the monthly salt means the id is
 * not stable across months.
 */

/**
 * The global that `data-before-send` names. The tracker resolves it by name off
 * `window` at send time (`window[beforeSend]`), so it is a string contract, not
 * a function reference.
 */
const BEFORE_SEND_HOOK = "__wrUmamiBeforeSend";

/** Same-origin, proxied by Caddy. Never a hostname — see the header comment. */
const TRACKER_SRC = "/u/script.js";

/**
 * Query parameters that survive into analytics. An **allowlist**, deliberately:
 * this app puts secrets in the query string.
 *
 * `/verify?token=…` and `/reset?token=…` carry single-use credentials, and
 * `?next=` carries wherever a player was heading. Umami records `url` including
 * its search string unless told otherwise, so a denylist here would be one new
 * screen away from shipping a password-reset token into a database — even one we
 * own. The acquisition data A79 ruling 7 actually asked for is the UTM set plus
 * `ref`, and that is all that is on this list.
 */
const KEEP_QUERY = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ref",
]);

/** `8-4-4-4-12` hex. Room ids and player ids both take this shape. */
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What an identifying path segment collapses to, so `/room/x` aggregates. */
const ID_PLACEHOLDER = ":id";

/**
 * Path segments whose *successor* is an identifier regardless of its shape.
 * `/room/:roomId` is the only parameterised route in `App.tsx` today; listing
 * the parent rather than pattern-matching the child means a room id that stops
 * being a uuid does not silently start being a page name.
 */
const PARENT_OF_ID = new Set(["room"]);

export interface UmamiPayload {
  url?: string;
  referrer?: string;
  [key: string]: unknown;
}

/**
 * Normalise one url-or-path for analytics: identifying segments collapse, and
 * every query parameter outside {@link KEEP_QUERY} is dropped.
 *
 * Absolute inputs come back as absolute (the tracker sends the referrer that
 * way); relative ones stay relative. Anything unparseable comes back as the
 * empty string rather than being passed through — a value we could not read is
 * exactly the value we should not forward.
 */
export function sanitiseTrackedUrl(raw: string): string {
  if (!raw) return "";

  // A dummy base lets one code path handle "/lobby?x=1" and a full URL. The
  // base is only re-serialised for inputs that were absolute to begin with.
  const wasAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
  let url: URL;
  try {
    url = new URL(raw, "https://relative.invalid");
  } catch {
    return "";
  }

  const segments = url.pathname.split("/");
  for (let i = 1; i < segments.length; i += 1) {
    const segment = segments[i];
    if (!segment) continue;
    if (UUID_SEGMENT.test(segment) || PARENT_OF_ID.has(segments[i - 1]?.toLowerCase() ?? "")) {
      segments[i] = ID_PLACEHOLDER;
    }
  }
  url.pathname = segments.join("/");

  for (const key of [...url.searchParams.keys()]) {
    if (!KEEP_QUERY.has(key.toLowerCase())) url.searchParams.delete(key);
  }

  // Fragments are never useful here and are the other place a token could hide.
  url.hash = "";

  return wasAbsolute ? url.toString() : `${url.pathname}${url.search}`;
}

/**
 * The `data-before-send` hook. Runs on every beacon, in the page, before the
 * fetch — so the sanitising above is what leaves the browser, not what the
 * server decides to keep.
 *
 * The referrer needs the same treatment as the url and for a less obvious
 * reason: on an in-app navigation the tracker sets `referrer` to the *previous*
 * in-app url, so `/verify?token=…` reaches the wire as a referrer one navigation
 * after it reaches it as a url. External referrers are left alone — they are the
 * acquisition signal this feature exists for.
 */
export function sanitisePayload(payload: UmamiPayload, origin: string): UmamiPayload {
  const next: UmamiPayload = { ...payload };

  if (typeof next.url === "string") next.url = sanitiseTrackedUrl(next.url);

  if (typeof next.referrer === "string" && next.referrer) {
    const sameOrigin = origin !== "" && next.referrer.startsWith(`${origin}/`);
    if (sameOrigin) next.referrer = sanitiseTrackedUrl(next.referrer);
  }

  return next;
}

/** The hook the tracker calls. Thin on purpose: {@link sanitisePayload} is the part with rules. */
export function beforeSend(_type: string, payload: UmamiPayload): UmamiPayload {
  return sanitisePayload(payload, typeof location === "undefined" ? "" : location.origin);
}

/**
 * Inject the tracker, or do nothing at all.
 *
 * Returns whether a script tag was added, which is what the tests assert on —
 * "the tag exists" and "no request was made" are the two states this function
 * has, and a boolean is the honest way to check the off one.
 *
 * Idempotent: a second call is a no-op, so React StrictMode's double-invoke or a
 * hot reload cannot double-count a page view.
 */
export function installAnalytics(doc: Document = document): boolean {
  if (!UMAMI_WEBSITE_ID) return false;
  if (doc.querySelector(`script[src="${TRACKER_SRC}"]`)) return false;

  // Assigned before the tag is appended: the tracker resolves the hook by name
  // at send time, but a `defer` script can still run before any later work here.
  (window as unknown as Record<string, unknown>)[BEFORE_SEND_HOOK] = beforeSend;

  const script = doc.createElement("script");
  script.defer = true;
  script.src = TRACKER_SRC;
  script.setAttribute("data-website-id", UMAMI_WEBSITE_ID);
  script.setAttribute("data-before-send", BEFORE_SEND_HOOK);
  doc.head.appendChild(script);

  return true;
}
